import React, { useCallback } from 'react';
import { useFormContext } from '../context/FormContext';
import { FormActionType } from '../context/formReducer';
import { ControlModeTemplateSettings } from '@src/types/ControlModes';

// Hook for setting-related operations
export function useSettings() {
  const { state, dispatch } = useFormContext();

  // Extract the setting-related state
  const {
    newSettingData,
    newSettingErrors,
    showAddSetting,
    isSettingEditMode,
    editingSetting,
    unsavedSettings,
    groupedFields,
  } = state;

  // Handle new setting change
  const handleNewSettingChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;

      dispatch({
        type: FormActionType.SET_NEW_SETTING_DATA,
        payload: { [name]: value },
      });

      // Clear error for this field if it exists
      if (newSettingErrors[name]) {
        const updatedErrors = { ...newSettingErrors };
        delete updatedErrors[name];
        dispatch({
          type: FormActionType.SET_NEW_SETTING_ERRORS,
          payload: updatedErrors,
        });
      }
    },
    [dispatch, newSettingErrors],
  );

  // Toggle add setting
  const toggleAddSetting = useCallback(() => {
    dispatch({ type: FormActionType.TOGGLE_ADD_SETTING });
  }, [dispatch]);

  // Set show add setting
  const setShowAddSetting = useCallback(
    (show: boolean) => {
      dispatch({ type: FormActionType.SET_SHOW_ADD_SETTING, payload: show });
    },
    [dispatch],
  );

  // Add new setting
  const addNewSetting = useCallback(() => {
    // Validate setting data
    const errors: Record<string, string> = {};

    if (!newSettingData.uiLabel.trim()) {
      errors.uiLabel = 'Label is required';
    }

    if (!newSettingData.controllerField.trim()) {
      errors.controllerField = 'Controller Field is required';
    } else {
      // Check if the controller field already exists
      const settingExists = unsavedSettings?.some(
        (s) =>
          s.controllerField === newSettingData.controllerField &&
          (!editingSetting ||
            s.controllerField !== editingSetting.controllerField),
      );

      if (settingExists) {
        errors.controllerField = 'Controller Field must be unique';
      }
    }

    if (Object.keys(errors).length > 0) {
      dispatch({
        type: FormActionType.SET_NEW_SETTING_ERRORS,
        payload: errors,
      });
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: 'Please correct the errors before adding the setting',
          severity: 'error',
        },
      });
      return;
    }

    // Create the new setting
    const newSetting: ControlModeTemplateSettings = {
      uiLabel: newSettingData.uiLabel,
      controllerField: newSettingData.controllerField,
      fields: [],
    };

    // Update settings
    const updatedSettings = unsavedSettings ? [...unsavedSettings] : [];

    if (isSettingEditMode && editingSetting) {
      // Update existing setting
      const settingIndex = updatedSettings.findIndex(
        (s) => s.controllerField === editingSetting.controllerField,
      );

      if (settingIndex !== -1) {
        // Preserve the fields from the original setting
        newSetting.fields = updatedSettings[settingIndex].fields;
        updatedSettings[settingIndex] = newSetting;
      }

      dispatch({
        type: FormActionType.SET_UNSAVED_SETTINGS,
        payload: updatedSettings,
      });
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: `Setting "${newSetting.uiLabel}" updated. Click Save to apply changes.`,
          severity: 'info',
        },
      });
    } else {
      // Add new setting
      updatedSettings.push(newSetting);
      dispatch({
        type: FormActionType.SET_UNSAVED_SETTINGS,
        payload: updatedSettings,
      });
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: `Setting "${newSetting.uiLabel}" added. Click Save to apply changes.`,
          severity: 'info',
        },
      });
    }

    // Update grouped fields
    const updatedGroupedFields = [...groupedFields];

    if (isSettingEditMode && editingSetting) {
      // Update existing group
      const groupIndex = updatedGroupedFields.findIndex(
        (g) => g.controllerField === editingSetting.controllerField,
      );

      if (groupIndex !== -1) {
        updatedGroupedFields[groupIndex] = {
          uiLabel: newSetting.uiLabel,
          controllerField: newSetting.controllerField,
          fields: newSetting.fields,
        };
      }
    } else {
      // Add new group
      updatedGroupedFields.push({
        uiLabel: newSetting.uiLabel,
        controllerField: newSetting.controllerField,
        fields: [],
      });
    }

    dispatch({
      type: FormActionType.SET_GROUPED_FIELDS,
      payload: updatedGroupedFields,
    });

    // Reset form
    dispatch({ type: FormActionType.RESET_NEW_SETTING_DATA });
    dispatch({ type: FormActionType.SET_NEW_SETTING_ERRORS, payload: {} });
    dispatch({ type: FormActionType.SET_SHOW_ADD_SETTING, payload: false });
    dispatch({ type: FormActionType.SET_IS_SETTING_EDIT_MODE, payload: false });
    dispatch({ type: FormActionType.SET_EDITING_SETTING, payload: null });
  }, [
    dispatch,
    newSettingData,
    unsavedSettings,
    isSettingEditMode,
    editingSetting,
    groupedFields,
  ]);

  // Start edit setting
  const startEditSetting = useCallback(
    (setting: ControlModeTemplateSettings) => {
      dispatch({
        type: FormActionType.SET_IS_SETTING_EDIT_MODE,
        payload: true,
      });
      dispatch({ type: FormActionType.SET_EDITING_SETTING, payload: setting });
      dispatch({ type: FormActionType.SET_SHOW_ADD_SETTING, payload: true });
      dispatch({
        type: FormActionType.SET_NEW_SETTING_DATA,
        payload: {
          uiLabel: setting.uiLabel,
          controllerField: setting.controllerField,
        },
      });
    },
    [dispatch],
  );

  // Delete setting
  const deleteSetting = useCallback(
    (controllerField: string) => {
      if (!unsavedSettings) {
        return;
      }

      // Find the setting
      const settingIndex = unsavedSettings.findIndex(
        (s) => s.controllerField === controllerField,
      );

      if (settingIndex === -1) {
        return;
      }

      // Remove the setting
      const updatedSettings = [...unsavedSettings];
      updatedSettings.splice(settingIndex, 1);

      dispatch({
        type: FormActionType.SET_UNSAVED_SETTINGS,
        payload: updatedSettings,
      });

      // Update grouped fields
      const updatedGroupedFields = groupedFields.filter(
        (group) => group.controllerField !== controllerField,
      );

      dispatch({
        type: FormActionType.SET_GROUPED_FIELDS,
        payload: updatedGroupedFields,
      });

      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: `Setting removed. Click Save to apply changes.`,
          severity: 'info',
        },
      });
    },
    [dispatch, unsavedSettings, groupedFields],
  );

  // Set new setting data
  const setNewSettingData = useCallback(
    (data: Partial<typeof newSettingData>) => {
      dispatch({ type: FormActionType.SET_NEW_SETTING_DATA, payload: data });
    },
    [dispatch],
  );

  // Set new setting errors
  const setNewSettingErrors = useCallback(
    (errors: Record<string, string>) => {
      dispatch({
        type: FormActionType.SET_NEW_SETTING_ERRORS,
        payload: errors,
      });
    },
    [dispatch],
  );

  return {
    // State
    newSettingData,
    newSettingErrors,
    showAddSetting,
    isSettingEditMode,
    editingSetting,

    // Handlers
    handleNewSettingChange,
    toggleAddSetting,
    setShowAddSetting,
    addNewSetting,
    startEditSetting,
    deleteSetting,
    setNewSettingData,
    setNewSettingErrors,
  };
}
